import { useState, useRef, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Loader2,
  Phone,
  Mail,
  Briefcase,
  FileText,
  StickyNote,
  Check,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useConversation, useSendMessage, useUpdateApplicantStatus, useUpdateNotes, useTemplates } from '../hooks/useApi';
import StatusBadge from '../components/StatusBadge';
import ResumeViewer from '../components/ResumeViewer';
import { formatDateTime, formatRelativeTime, cn } from '../lib/utils';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'responded', label: 'Responded' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' },
];

export default function ConversationDetailPage() {
  const { applicantId } = useParams<{ applicantId: string }>();
  const [newMessage, setNewMessage] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useConversation(applicantId!);
  const sendMessage = useSendMessage();
  const updateStatus = useUpdateApplicantStatus();
  const updateNotes = useUpdateNotes();
  const { data: templatesData } = useTemplates();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages]);

  useEffect(() => {
    if (data?.applicant?.notes) {
      setNotes(data.applicant.notes);
    }
  }, [data?.applicant?.notes]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !applicantId) return;

    await sendMessage.mutateAsync({
      applicantId,
      message: newMessage.trim(),
    });

    setNewMessage('');
  };

  const handleStatusChange = async (status: string) => {
    if (!applicantId) return;
    await updateStatus.mutateAsync({ applicantId, status });
  };

  const handleSaveNotes = async () => {
    if (!applicantId) return;
    await updateNotes.mutateAsync({ applicantId, notes });
    setShowNotes(false);
  };

  const handleUseTemplate = (content: string) => {
    const processed = content
      .replace(/\{\{firstName\}\}/g, data?.applicant?.first_name || '')
      .replace(/\{\{phone\}\}/g, data?.applicant?.phone || '');
    setNewMessage(processed);
    setShowTemplates(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  const { applicant, messages } = data || {};

  if (!applicant) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Applicant not found</p>
        <Link to="/conversations" className="text-primary-600 hover:underline mt-2 inline-block">
          Back to conversations
        </Link>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-6rem)] lg:h-[calc(100vh-8rem)] flex flex-col">
      {/* Header */}
      <div className="flex flex-col gap-3 mb-4 lg:mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/conversations" className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div className="min-w-0">
              <h2 className="text-base lg:text-lg font-semibold text-gray-900 truncate">
                {applicant.first_name} {applicant.last_name}
              </h2>
              <div className="hidden sm:flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                  <Briefcase className="w-4 h-4" />
                  {applicant.job_title}
                </span>
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {applicant.phone}
                </span>
                <span className="flex items-center gap-1 hidden md:flex">
                  <Mail className="w-4 h-4" />
                  {applicant.email}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setShowNotes(!showNotes)}
              className={cn(
                'hidden sm:flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
                showNotes ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              )}
            >
              <StickyNote className="w-4 h-4" />
              <span className="hidden md:inline">Notes</span>
            </button>
            <select
              value={applicant.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="input text-sm py-1.5 w-28"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Mobile: collapsible details */}
        <button
          onClick={() => setShowDetails(!showDetails)}
          className="sm:hidden flex items-center gap-2 text-sm text-gray-500 px-2"
        >
          {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {applicant.job_title} &middot; {applicant.phone}
        </button>
        {showDetails && (
          <div className="sm:hidden flex flex-col gap-2 px-2 text-sm text-gray-500">
            <span className="flex items-center gap-2"><Mail className="w-4 h-4" />{applicant.email}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setShowNotes(!showNotes)}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors',
                  showNotes ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-700'
                )}
              >
                <StickyNote className="w-4 h-4" />
                Notes
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 flex-1 min-h-0">
        {/* Main chat area */}
        <div className="flex-1 card overflow-hidden flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-3 lg:p-4 space-y-3 lg:space-y-4">
            {messages?.map((message, index) => {
              const isOutbound = message.direction === 'outbound';
              const showDate = index === 0 ||
                new Date(messages[index - 1].sent_at).toDateString() !==
                new Date(message.sent_at).toDateString();

              return (
                <div key={message.id}>
                  {showDate && (
                    <div className="flex justify-center my-4">
                      <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                        {formatDateTime(message.sent_at).split(',')[0]}
                      </span>
                    </div>
                  )}
                  <div
                    className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`message-bubble max-w-[85%] lg:max-w-[70%] px-3 lg:px-4 py-2 lg:py-2.5 rounded-2xl ${
                        isOutbound
                          ? 'bg-primary-600 text-white rounded-br-md'
                          : 'bg-gray-100 text-gray-900 rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm">{message.content}</p>
                      <div
                        className={`flex items-center gap-1 mt-1 text-xs ${
                          isOutbound ? 'text-primary-200' : 'text-gray-500'
                        }`}
                      >
                        <span>{formatRelativeTime(message.sent_at)}</span>
                        {isOutbound && (
                          <span>
                            {message.status === 'sent' && ' \u2022 Sent'}
                            {message.status === 'delivered' && ' \u2022 Delivered'}
                            {message.status === 'read' && ' \u2022 Read'}
                            {message.status === 'failed' && ' \u2022 Failed'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {messages?.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">No messages yet</p>
                <p className="text-sm text-gray-400 mt-1">
                  Send a message to start the conversation
                </p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message input */}
          <form
            onSubmit={handleSendMessage}
            className="p-3 lg:p-4 border-t border-gray-200 bg-gray-50"
          >
            {showTemplates && (
              <div className="mb-3 bg-white rounded-lg border border-gray-200 shadow-lg max-h-48 overflow-y-auto">
                <div className="p-2 text-xs font-medium text-gray-500 border-b">
                  Message Templates
                </div>
                {templatesData?.templates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => handleUseTemplate(template.content)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b last:border-0"
                  >
                    <span className="font-medium">{template.name}</span>
                    <p className="text-xs text-gray-500 truncate">{template.content}</p>
                  </button>
                ))}
                {!templatesData?.templates?.length && (
                  <div className="p-3 text-sm text-gray-500 text-center">
                    No templates yet
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 lg:gap-3">
              <button
                type="button"
                onClick={() => setShowTemplates(!showTemplates)}
                className="hidden sm:block px-3 py-2 text-sm text-gray-600 hover:bg-gray-200 rounded-lg whitespace-nowrap"
              >
                Templates
              </button>
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 input"
              />
              <button
                type="submit"
                disabled={!newMessage.trim() || sendMessage.isPending}
                className="btn-primary px-3 lg:px-4"
              >
                {sendMessage.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Sidebar - hidden on mobile unless toggled */}
        <div className="hidden lg:block w-80 space-y-4">
          {/* Resume */}
          <div className="card p-4">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Resume
            </h3>
            <ResumeViewer
              applicantId={applicantId!}
              resumeKey={applicant.resume_key}
              applicantName={`${applicant.first_name} ${applicant.last_name}`}
            />
          </div>

          {/* Notes */}
          {showNotes && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900 flex items-center gap-2">
                  <StickyNote className="w-4 h-4" />
                  Notes
                </h3>
                <button
                  onClick={handleSaveNotes}
                  disabled={updateNotes.isPending}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
                >
                  {updateNotes.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Save
                </button>
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about this applicant..."
                className="input min-h-[120px] text-sm"
              />
            </div>
          )}

          {/* Applicant Info */}
          <div className="card p-4">
            <h3 className="font-medium text-gray-900 mb-3">Applicant Info</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Source</span>
                <span className="capitalize">{applicant.source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Applied</span>
                <span>{formatDateTime(applicant.created_at)}</span>
              </div>
              {applicant.scheduled_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Interview</span>
                  <span>{formatDateTime(applicant.scheduled_at)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile notes panel */}
      {showNotes && (
        <div className="lg:hidden card p-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-gray-900 flex items-center gap-2">
              <StickyNote className="w-4 h-4" />
              Notes
            </h3>
            <button
              onClick={handleSaveNotes}
              disabled={updateNotes.isPending}
              className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
            >
              {updateNotes.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Check className="w-3 h-3" />
              )}
              Save
            </button>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes about this applicant..."
            className="input min-h-[100px] text-sm"
          />
        </div>
      )}
    </div>
  );
}
